import { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import { X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STORAGE_KEY = 'promo_banner_closed';
const ADMIN_STORAGE_KEY = 'promo_banner_closed_admin';

export const PromoBanner = () => {
  const [banner, setBanner] = useState(null);
  const [visible, setVisible] = useState(false);
  const [closing, setClosing] = useState(false);
  const isLoggedIn = !!localStorage.getItem('admin_token');
  const location = useLocation();
  const isAdminPage = location.pathname.startsWith('/admin');

  useEffect(() => {
    // Don't fetch banner on admin pages
    if (isAdminPage) return;
    fetchBanner();
  }, [isAdminPage]);

  const fetchBanner = async () => {
    try {
      const res = await axios.get(`${API}/promo-banner`);
      const bannerData = res.data;
      
      // Only show if banner is active AND has an image
      if (!bannerData.active || (!bannerData.image_url && !bannerData.mobile_image_url)) {
        return;
      }

      // Check if admin has closed the banner in this session
      if (isLoggedIn) {
        const adminClosedAt = sessionStorage.getItem(ADMIN_STORAGE_KEY);
        if (adminClosedAt) {
          return; // Admin closed it in this session, don't show
        }
      } else if (!bannerData.show_always) {
        // For visitors: respect the 24h cooldown if show_always is false
        const closedAt = localStorage.getItem(STORAGE_KEY);
        if (closedAt) {
          const closedTime = parseInt(closedAt, 10);
          const now = Date.now();
          const hoursElapsed = (now - closedTime) / (1000 * 60 * 60);
          if (hoursElapsed < 24) {
            return; // Don't show, still within 24h cooldown
          }
        }
      }

      setBanner(bannerData);
      // Show banner after a short delay
      setTimeout(() => setVisible(true), 2000);
    } catch (error) {
      console.error('Error fetching promo banner:', error);
    }
  };

  const handleClose = () => {
    setClosing(true);
    
    if (isLoggedIn) {
      // For admins: use sessionStorage so it stays closed during the session
      sessionStorage.setItem(ADMIN_STORAGE_KEY, Date.now().toString());
    } else if (banner && !banner.show_always) {
      // For visitors: use localStorage with 24h cooldown (only if not show_always)
      localStorage.setItem(STORAGE_KEY, Date.now().toString());
    }
    
    // Animate out then hide
    setTimeout(() => setVisible(false), 300);
  };

  // Don't render anything on admin pages or if no banner
  if (isAdminPage || !banner || !visible) return null;

  const imageUrl = window.innerWidth < 640 
    ? (banner.mobile_image_url || banner.image_url) 
    : banner.image_url;

  return (
    <div 
      className={`fixed bottom-0 left-0 right-0 z-50 transition-transform duration-300 ${
        closing ? 'translate-y-full' : 'translate-y-0'
      }`}
      style={{ animation: closing ? 'none' : 'slideUp 0.3s ease-out' }}
    >
      {/* Banner Container */}
      <div className="relative bg-black border-t-4 border-white">
        {/* Close Button */}
        <button
          onClick={handleClose}
          className="absolute top-2 right-2 z-10 p-1.5 bg-black/80 hover:bg-black text-white rounded-full transition-colors"
          aria-label="Cerrar banner"
          data-testid="close-promo-banner"
        >
          <X size={20} />
        </button>

        {/* Banner Image/Link */}
        {banner.link_url ? (
          <a 
            href={banner.link_url}
            target="_blank"
            rel="noopener noreferrer"
            className="block"
            data-testid="promo-banner-link"
          >
            <img
              src={imageUrl}
              alt="Promoción"
              className="w-full h-auto max-h-32 sm:max-h-40 object-contain"
            />
          </a>
        ) : (
          <img
            src={imageUrl}
            alt="Promoción"
            className="w-full h-auto max-h-32 sm:max-h-40 object-contain"
          />
        )}
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
          }
          to {
            transform: translateY(0);
          }
        }
      `}</style>
    </div>
  );
};
